import { effectiveScrollY } from "@/lib/hooks/use-scroll-lock"
import type { BrowseSnapshotData } from "@/platform/bridge/types"

interface Entry {
  id: string
  depth: number
  url: string
}

interface Snapshot {
  scrollY: number
  anchor?: { selector: string; offset: number }
  values: Record<string, number>
  rails: ReadonlyArray<{ key: string; left: number }>
}

const entries = new Map<string, Snapshot>()
const captures = new Map<() => void, string>()
const readiness = new Map<() => boolean, string>()
let current: Entry = { id: "root", depth: 0, url: "" }
let revision = 0
let rootUrl: string | undefined
let departing: string | undefined
let traversing = false
let negotiated = false
let restoring: (() => void) | undefined
let scheduleRestoration: (() => void) | undefined
const listeners = new Set<() => void>()
const snapshotListeners = new Set<() => void>()
const SNAPSHOT_BYTES = 4 * 1024 * 1024

const awayFromRoot = () => rootUrl !== undefined && new URL(current.url).pathname !== new URL(rootUrl).pathname
export const browseHistory = () => ({ entryId: current.id, revision, canGoBack: current.depth > 0 || awayFromRoot() })
export const browseEntryId = () => current.id
export const hasBrowseHost = () => negotiated
export const resetBrowseHost = () => {
  negotiated = false
}
export const negotiateBrowseHost = () => {
  negotiated = true
  for (const listener of listeners) listener()
}
export const subscribeBrowseHistory = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const snapshot = (owner = current.id): Snapshot => {
  let value = entries.get(owner)
  if (!value) {
    value = { scrollY: 0, values: {}, rails: [] }
    entries.set(owner, value)
  }
  return value
}

export const readBrowseValue = (key: string, initial: number, owner = current.id): number => entries.get(owner)?.values[key] ?? initial
export const writeBrowseValue = (key: string, value: number, owner = current.id): void => {
  snapshot(owner).values[key] = value
}
export const registerBrowseCapture = (capture: () => void, owner = current.id) => {
  captures.set(capture, owner)
  return () => {
    captures.delete(capture)
  }
}
export const registerBrowseReadiness = (ready: () => boolean, owner = current.id) => {
  readiness.set(ready, owner)
  return () => {
    readiness.delete(ready)
  }
}
export const notifyBrowseReadiness = (): void => {
  scheduleRestoration?.()
}

export const subscribeBrowseSnapshots = (listener: () => void) => {
  snapshotListeners.add(listener)
  return () => {
    snapshotListeners.delete(listener)
  }
}

export const railKey = (element: Element): string => {
  const scope = element.closest("[data-feed-section], [data-feed-row]")
  const root = scope ?? document.body
  const key = scope?.getAttribute("data-feed-section") ?? (scope ? `row:${scope.getAttribute("data-feed-row")}` : "page")
  const rails = Array.from(root.querySelectorAll(".reel")).filter((rail) => rail.closest("[data-feed-section], [data-feed-row]") === scope)
  return `${key}:${rails.indexOf(element)}`
}

const capture = () => {
  for (const [save, owner] of captures) if (owner === current.id) save()
  const value = snapshot()
  value.scrollY = effectiveScrollY()
  let anchor: { element: HTMLElement; rect: DOMRect } | undefined
  for (const element of document.querySelectorAll<HTMLElement>("[data-nelo-item], [data-feed-section]")) {
    const rect = element.getBoundingClientRect()
    if (rect.bottom > 0 && rect.top < window.innerHeight && (!anchor || Math.abs(rect.top) < Math.abs(anchor.rect.top))) anchor = { element, rect }
  }
  if (anchor) {
    const attribute = anchor.element.hasAttribute("data-nelo-item") ? "data-nelo-item" : "data-feed-section"
    const section = anchor.element.closest("[data-feed-section]")
    const scope = section && attribute !== "data-feed-section" ? `[data-feed-section="${CSS.escape(section.getAttribute("data-feed-section") ?? "")}"] ` : ""
    value.anchor = { selector: `${scope}[${attribute}="${CSS.escape(anchor.element.getAttribute(attribute) ?? "")}"]`, offset: anchor.rect.top }
  } else delete value.anchor
  value.rails = Array.from(document.querySelectorAll<HTMLElement>(".reel"), (element) => ({ key: railKey(element), left: element.scrollLeft }))
  let size = JSON.stringify(Array.from(entries)).length * 2
  for (const [id, entry] of entries) {
    if (size <= SNAPSHOT_BYTES) break
    if (id === current.id) continue
    size -= JSON.stringify(entry).length * 2
    entries.delete(id)
  }
}

export const captureBrowseSnapshot = (): BrowseSnapshotData | undefined => {
  if (!traversing && !restoring && departing !== current.id) capture()
  const value = snapshot()
  const result = { ...value, values: Object.entries(value.values).map(([key, value]) => ({ key, value })) }
  return JSON.stringify(result).length < 48 * 1024 ? result : undefined
}

export const prepareBrowseNavigation = (): void => {
  if (!traversing && !restoring) capture()
  departing = current.id
}

export const restoreBrowseSnapshot = (value: BrowseSnapshotData): void => {
  restoring?.()
  entries.set(current.id, { ...value, values: Object.fromEntries(value.values.map(({ key, value }) => [key, value])) })
  traversing = true
  for (const listener of snapshotListeners) listener()
  restoreBrowsePosition()
}

export const goBackInBrowseHistory = (navigate: (url: string) => void): void => {
  if (current.depth > 0) {
    prepareBrowseNavigation()
    window.history.back()
  } else if (awayFromRoot() && rootUrl) navigate(rootUrl)
}

export const returnToBrowseRoot = (navigate?: (url: string) => void): void => {
  if (current.depth > 0) {
    prepareBrowseNavigation()
    window.history.go(-current.depth)
  } else if (awayFromRoot() && rootUrl && navigate) navigate(rootUrl)
  else window.scrollTo({ top: 0, behavior: "smooth" })
}

export const clearBrowseSnapshots = (): void => {
  restoring?.()
  traversing = false
  departing = undefined
  entries.clear()
  revision++
  for (const listener of snapshotListeners) listener()
}

export const installBrowseHistory = (root?: string): (() => void) => {
  departing = undefined
  rootUrl = root ? new URL(root, window.location.href).href : undefined
  const push = window.history.pushState.bind(window.history)
  const replace = window.history.replaceState.bind(window.history)
  const previousRestoration = window.history.scrollRestoration
  window.history.scrollRestoration = "manual"
  current = window.history.state?.neloBrowse ?? { id: crypto.randomUUID(), depth: 0, url: window.location.href }
  replace({ ...window.history.state, neloBrowse: current }, "")
  const commit = (state: Record<string, unknown> | null, title: string, url: string | URL | null | undefined, advance: boolean) => {
    const nextUrl = url == null ? window.location.href : new URL(url, window.location.href).href
    if (advance || nextUrl !== current.url) {
      if (!traversing && !restoring && departing !== current.id) capture()
      restoring?.()
      traversing = false
    }
    if (advance) current = { id: crypto.randomUUID(), depth: current.depth + 1, url: nextUrl }
    else current = { ...current, url: nextUrl }
    const write = advance ? push : replace
    write({ ...state, neloBrowse: current }, title, url)
    departing = undefined
    revision++
    for (const listener of listeners) listener()
  }
  window.history.pushState = (state, title, url) => commit(state, title, url, true)
  window.history.replaceState = (state, title, url) => commit(state, title, url, false)
  const pop = (event: PopStateEvent) => {
    if (!traversing && !restoring && departing !== current.id) capture()
    restoring?.()
    departing = undefined
    const entry = event.state?.neloBrowse as Entry | undefined
    current = entry ?? { id: crypto.randomUUID(), depth: 0, url: window.location.href }
    replace({ ...window.history.state, neloBrowse: current }, "")
    revision++
    traversing = true
    for (const listener of listeners) listener()
  }
  window.addEventListener("popstate", pop)
  return () => {
    restoring?.()
    window.history.pushState = push
    window.history.replaceState = replace
    window.history.scrollRestoration = previousRestoration
    window.removeEventListener("popstate", pop)
  }
}

export const restoreBrowsePosition = (): void => {
  if (!traversing) return
  traversing = false
  const value = entries.get(current.id)
  if (!value) return
  let frame = 0
  const cleanup = () => {
    cancelAnimationFrame(frame)
    observer.disconnect()
    mutations.disconnect()
    window.removeEventListener("touchstart", cleanup)
    window.removeEventListener("wheel", cleanup)
    window.removeEventListener("keydown", cleanup)
    if (restoring === cleanup) {
      restoring = undefined
      scheduleRestoration = undefined
    }
  }
  const restore = () => {
    for (const [ready, owner] of readiness) if (owner === current.id && !ready()) return
    for (const element of document.querySelectorAll<HTMLElement>(".reel")) {
      const rail = value.rails.find((item) => item.key === railKey(element))
      if (rail) element.scrollLeft = Math.min(rail.left, Math.max(0, element.scrollWidth - element.clientWidth))
    }
    const anchor = value.anchor ? document.querySelector(value.anchor.selector) : null
    const max = document.documentElement.scrollHeight - window.innerHeight
    window.scrollTo({ top: Math.min(value.scrollY, Math.max(0, max)), behavior: "instant" })
    if (anchor && value.anchor) window.scrollBy({ top: anchor.getBoundingClientRect().top - value.anchor.offset, behavior: "instant" })
    cleanup()
  }
  const schedule = () => {
    cancelAnimationFrame(frame)
    frame = requestAnimationFrame(restore)
  }
  const observer = new ResizeObserver(schedule)
  const mutations = new MutationObserver(schedule)
  mutations.observe(document.body, { childList: true, subtree: true })
  observer.observe(document.body)
  window.addEventListener("touchstart", cleanup, { passive: true })
  window.addEventListener("wheel", cleanup, { passive: true })
  window.addEventListener("keydown", cleanup)
  restoring = cleanup
  scheduleRestoration = schedule
  frame = requestAnimationFrame(restore)
}
